/* Copyright 2012-2018 The MathWorks, Inc. */

/**
 * This is a widget which opens up as a result of the click action of the gallery drop down.
 * It has a search bar for filtering category items.
 * It supports two views: LIST and ICON
 */

define([
    "dojo/_base/declare",
    "dojo/on",
    "dojo/keys",
    "dojo/aspect",
    "MW/toolstrip/utils/DragSorter",
    "dijit/registry",
    "dijit/_WidgetBase",
    "dijit/_TemplatedMixin",
    "dijit/_Container",
    "dojo/i18n!l10n/nls/toolstrip",
    "dojo/text!./templates/GalleryPanel.html",
    "mw-form/TextField",
    "mw-form/ToggleButton",
    "mw-form/PushButton",
    "MW/toolstrip/form/SearchTextField",
    "mw-log/Log",
    "MW/toolstrip/constants/gallery/GalleryViewConstants",
    "MW/toolstrip/mixins/property/OptionsEnabledMixin",
    "MW/toolstrip/widgets/_WidgetMixin",
    "MW/toolstrip/widgets/gallery/GalleryItemRowCountMixin",
    "MW/toolstrip/widgets/gallery/GalleryItemTextLineCountMixin",
    "MW/toolstrip/constants/gallery/ListViewDisplayDensityConstants",
    "MW/toolstrip/constants/TypeConstants",
    "MW/uiframework/utils"
], function (declare, on, keys, aspect, DragSorter, registry, _WidgetBase, _TemplatedMixin, _Container, toolstripL10n,
             template, TextField, ToggleButton, PushButton, SearchTextField, Log, GalleryViewConstants, OptionsEnabledMixin, _WidgetMixin,
             GalleryItemRowCountMixin, GalleryItemTextLineCountMixin, ListViewDisplayDensityConstants, TypeConstants, utils) {

    function capitalizeFirstLetter(string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    };

    var GalleryFilter = declare(null, {
        /** @lends Filter# */
        /**
         * A filter object is used to define the criteria for filtering of gallery items.
         * @constructs
         * @param {object} args All properties are required except 'disabled'.
         *     <li>id: The id of the filter.
         *     <li>disabled: The current status of the filter. This determines whether the filter
         *                   will be applied or not.
         *     <li>checkerFunction: This is the function which takes in a gallery item and returns
         *                         a Boolean value which determines whether the given gallery item
         *                         passes the custom criteria or not.
         */
        constructor: function (args) {
            args = args || {};

            Log.assert(args.id, "An id is required for a filter!");
            Log.assert(args.checkerFunction, "A checker function is required for a filter!");

            /**
             * The id of the filter.
             * @type string|number
             */
            this.id = args.id;

            /**
             * The current status of the filter. This determines whether the filter will be applied
             * or not.
             * @type boolean
             * @default false
             */
            this.disabled = !!args.disabled;

            /**
             * This is the function which takes in a gallery item and returns a Boolean value which
             * determines whether the given gallery item passes the custom criteria or not.
             * @type function
             */
            this.checkerFunction = args.checkerFunction;
        }
    });

    return declare([_WidgetBase, _TemplatedMixin, _Container, _WidgetMixin, GalleryItemRowCountMixin,
        GalleryItemTextLineCountMixin, OptionsEnabledMixin], {

        templateString: template,
        showHeader: true,
        dndEnabled: false,
        ownsFooter: true,
        _listeners: [],

        /** @lends GalleryPopupWidget# */
        /**
         * Represents a Gallery Popup Widget. This is a widget which opens up as a result of
         * the click action of the gallery drop down. It has a search bar for filtering categoryl
         * items. It supports two views: LIST and ICON
         */

        constructor: function() {
            this.searchBox = new SearchTextField({
                value: ""
            });

            // We need to override SizeMixin._setWidthAttr for gallery search box to use css for width
            this.searchBox._setWidthAttr = function () {/* No-op */};

            this.own(this.searchBox);
            this._searchBoxValue = "";

            this._buildAndAddFilters();
        },
        
        buildRendering: function(){
            this.inherited(arguments);
            this.searchBox.placeAt(this.searchTextFieldContainerNode);
        },

        /**
         * @override
         */
        postCreate: function () {
            this.domNode.classList.add("galleryPanel");
            this.domNode.classList.add("displayFocus");
            this.inherited(arguments);

            this._setFocusType();

            this._createSearchBoxAndButtons();

            this.own(aspect.after(this, "addChild", function (widget, index, originator) {
                widget.set("displayState", this.displayState);
                widget.set("reorderCategory", this.reorderCategory);
                widget.set("galleryItemRowCount", this.galleryItemRowCount);
                widget.set("galleryPopupId", this.id);

                // Only fire Gallery State Changed event when the state is changed from a user interaction
                if (originator && originator.source === "UI") {
                    this.galleryStateChanged();
                } else if (this.refreshGallery) {
                    this.refreshGallery();
                }

                this._createDndSource();
            }.bind(this), true));

            this.own(aspect.after(this, "removeChild", function (widget, originator) {
                if (!this._moving) {
                    // Only fire Gallery State Changed event when the state is changed from a user interaction
                    if (originator && originator.source === "UI") {
                        this.galleryStateChanged();
                    } else if (this.refreshGallery) {
                        this.refreshGallery();
                    }
                } else {
                    delete this._moving;
                }
            }.bind(this), true));

            this.noSearchMatchNode.style.display = "none";
            this.noSearchMatchNode.textContent = toolstripL10n.noSearchMatchNode;

            this._startupHeaderWidgets();

            this._listeners.push(utils.addEventListener(this.domNode, "keydown", this._keyDownHandler.bind(this)));

            // categoryContainer can grow with more categories and gallery items. If it grows to a point where it
            // gets a scroll bar, firefox has a behavior where it injects this div in the focus order even if there
            // is no tabindex set on it. To avoid this, explicitly set a tabindex of -1 to categoryContainer.
            this.categoryContainer.setAttribute("tabindex", "-1");
        },

        _keyDownHandler: function (e) {
            if (e.keyCode === keys.TAB) {
                var firstFocusableElement = this.header.querySelector("[tabindex='0']");
                var lastFocusableElement = this._getLastFocusableElement();

                if (firstFocusableElement && lastFocusableElement) {
                    if (e.shiftKey) {
                        if (document.activeElement === firstFocusableElement) {
                            lastFocusableElement.focus();
                            e.preventDefault();
                        }
                    } else {
                        if (document.activeElement === lastFocusableElement) {
                            firstFocusableElement.focus();
                            e.preventDefault();
                        }
                    }
                }

                if (this._isInPopup()) {
                    // Not stopping immediate propagation lets the dojo code close the popup on "Tab" key press
                    e.stopImmediatePropagation();
                }
            }
        },

        _isInPopup: function () {
            return false;
        },

        _getLastFocusableElement: function () {
            var footerFocusableChildren = this.footerContainer.querySelectorAll("[tabindex='0']");
            footerFocusableChildren = Array.prototype.slice.call(footerFocusableChildren);

            if (footerFocusableChildren && footerFocusableChildren.length > 0) {
                return footerFocusableChildren[footerFocusableChildren.length - 1]
            } else {
                var visibleCategories = this.domNode.querySelectorAll(".Category:not(.hideFromView)");
                visibleCategories = Array.prototype.slice.call(visibleCategories);
                if (visibleCategories && visibleCategories.length > 0) {
                    var lastVisibleCategory = visibleCategories[visibleCategories.length - 1];
                    var visibleGalleryItemsAndFavNodes = lastVisibleCategory.querySelectorAll(".mwGalleryItem:not(.hideFromView) [tabindex='0']");
                    visibleGalleryItemsAndFavNodes = Array.prototype.slice.call(visibleGalleryItemsAndFavNodes);

                    if (visibleGalleryItemsAndFavNodes && visibleGalleryItemsAndFavNodes.length > 0) {
                        if (this.get("favoritesEnabled")) {
                            return visibleGalleryItemsAndFavNodes[visibleGalleryItemsAndFavNodes.length - 1]; // favButtonNode of last gallery item
                        } else {
                            return visibleGalleryItemsAndFavNodes[visibleGalleryItemsAndFavNodes.length - 2]; // last gallery item
                        }

                    } else {
                        // movetop/movebottom nodes
                        var moveBottomButtonNode = lastVisibleCategory.querySelector(".moveBottomButton");
                        var moveTopButtonNode = lastVisibleCategory.querySelector(".moveTopButton");
                        if (moveBottomButtonNode && window.getComputedStyle(moveBottomButtonNode).display !== "none") {
                            return moveBottomButtonNode;
                        } else if (moveTopButtonNode && window.getComputedStyle(moveTopButtonNode).display !== "none") {
                            return moveTopButtonNode;
                        } else {
                            // category header
                            return lastVisibleCategory.querySelector(".titleNode");
                        }
                    }
                } else {
                    // last node in the gallery panel header
                    var focusableHeaderElements = this.header.querySelectorAll("[tabindex='0']");
                    if (focusableHeaderElements && focusableHeaderElements.length > 0) {
                        return focusableHeaderElements[focusableHeaderElements.length - 1];
                    }
                }
            }
        },

        _setFocusType: function () {
            this.domNode.setAttribute("data-focus", "permanent");
        },

        /**
         * Builds a search filter and adds it to the list of filters to be checked against for
         * determining the items to be displayed.
         */
        _buildAndAddFilters: function () {
            var that = this;

            this.galleryItemFilters = {};

            this.galleryItemFilters.search = new GalleryFilter({
                id: "search",
                checkerFunction: function (item) {
                    var text, regexp;
                    text = that._searchBoxValue;

                    // g1895232: The search field is just a one line field. For gallery items with multiple lines
                    // the regular expression would fail if someone uses space instead of new line character. Since
                    // one cannot add new line characters while searching, replace the new line characters with a space.
                    var updatedItemText = item.get("text").replace(/\n/g, " ");
                    // It is considered a match if any of the following condition is met:
                    // 1. The search string is empty ""
                    // 2. The search string is a substring of the text of the item (with new line character treated
                    // as a space)
                    // 3. The search string is a substring of the description of the item
                    // 4. The search string is a substring of the title of the item's parent category
                    regexp = new RegExp(that._escapeRegExp(text), 'i');
                    return (text === "" || regexp.test(updatedItemText) || regexp.test(item.get("description")) || regexp.test(item.getParent().get("title")));
                }
            });

            this.galleryItemFilters.onlyShowEnabled = new GalleryFilter({
                id: "onlyShowEnabled",
                disabled: true,
                checkerFunction: function (item) {
                    return item.get("disabled") === false;
                }
            });
        },

        //this function is to check if the Gallery footer has PopupList inside the footer container
        //If so, call the popuplist's onOpen()
        _checkForPopupListInFooter: function () {
            var hasPopupList, popuplist, widgetId, widget;
            if (this.footerContainer) {
                hasPopupList = this.footerContainer.querySelectorAll(".toolstripPopupList").length > 0 ? true : false;
                if (hasPopupList) {
                    popuplist = this.footerContainer.querySelector(".toolstripPopupList");
                    widgetId = popuplist.getAttribute("widgetid");
                    widget = registry.byId(widgetId);
                    //Call the PopupList's onOpen to trigger all the functions that happen after popupList open
                    widget.onOpen();
                }
            }
        },

        enforceFilters: function () {
            var items, categories;

            categories = this.getChildren();
            if (this._favCategoryWidget) {
                categories.push(this._favCategoryWidget);
            }

            categories.forEach(function (category) {
                items = category.getChildren();
                items.forEach(function (item) {
                    item.domNode.classList.remove("hideFromView");
                    Object.keys(this.galleryItemFilters).forEach(function (key) {
                        var filter = this.galleryItemFilters[key];
                        if (!filter.disabled && !filter.checkerFunction(item)) {
                            item.domNode.classList.add("hideFromView");
                        }
                    }, this);
                }, this);

                // If all the items in a category are hidden then hide the category as well.
                if ((items.length < 1 && category.hideWhenEmpty) ||
                    (((this._searchBoxValue.length > 0) || (items.length > 0)) &&
                    (category.domNode.querySelectorAll(".hideFromView").length >= items.length))) {
                    category.domNode.classList.add("hideFromView");
                } else {
                    category.domNode.classList.remove("hideFromView");
                }
            }, this);

            if (!this._invokingGallery && this.get("columnMajorLayout") && this.updateColumnMajorLayout) {
                // For column major layout (currently only supported for widgets other than gallery that open
                // GalleryPopup), when any search within the Gallery Popup updates the search results, update the
                // layout again to make sure the currently visible Gallery Items grid within the category are
                // correctly layed out.
                this.updateColumnMajorLayout();
            }

            // If all the categories are hidden then show the no match label node
            this._toggleDisplay(this.noSearchMatchNode, this.domNode.querySelectorAll(".Category.hideFromView").length === categories.length);
        },

        _setReorderCategory: function (value) {
            if (typeof value !== "boolean") {
                throw new Error("Expected input value for 'reorderCategory' to be a boolean value.");
            }

            this._set("reorderCategory", value);
        },

        _setFavoritesEnabledAttr: function (value) {
            if (typeof value !== "boolean") {
                throw new Error("Expected input value for 'favoritesEnabled' to be a boolean value.");
            }

            if (value === true) {
                this.domNode.classList.add("favoritesEnabled");
                this.domNode.classList.remove("favoritesDisabled");
            } else {
                this.domNode.classList.add("favoritesDisabled");
                this.domNode.classList.remove("favoritesEnabled");
            }

            this._set("favoritesEnabled", value);
        },

        _setShowHeaderAttr: function(value) {
            if (typeof value !== "boolean") {
                throw new Error("Expected input value for 'showHeader' to be a boolean value.");
            }

            if (value === false) {
                this.header.style.display = "none";
                this.header.classList.remove("margin");
            } else if (value === true) {
                this.header.style.display = null;
                this.header.classList.add("margin");
            }

            this._set("showHeader", value);
        },

        _setHideDisabledItemsAttr: function (value) {
            if (value == true) {
                this.galleryItemFilters.onlyShowEnabled.disabled = false;
                this.enforceFilters();
            } else {
                this.galleryItemFilters.onlyShowEnabled.disabled = true;
                this.enforceFilters();
            }

            if (this._favCategoryWidget) {
                this._favCategoryWidget.showHideCategory();
            }

            this._set("hideDisabledItems", value);
        },

        _setDisplayStateAttr: function (value) {
            var categoryContainer = this.categoryContainer;
            // Remove scrollbar before any display state change to allow proper node placement
            categoryContainer.style.overflowY = "hidden";

            //TODO: Check if we can avoid doing this for same value set
            this.domNode.classList.remove(GalleryViewConstants.ICON);
            this.domNode.classList.remove(GalleryViewConstants.LIST);

            this.domNode.classList.add(value);

            // g2030459: Setting the display state as of the GalleryPanel before the updating state of its 
            // children because GalleryItem depends on the correct state of GalleryPopup being set.

            this._set("displayState", value);

            if (this._favCategoryWidget) {
                this._favCategoryWidget.set("displayState", value);
            }

            this.getChildren().forEach(function (category) {
                category.set("displayState", value);
            });
            
            this._updateToggleButtons();
            
            // g2041157: Apply columnMajor layout if it was not applied before to icon view. This can happen when 
            // the gallery popup opens in list view and has columnMajorLayout property set which applies only to icon
            // view.
            if (this.updateColumnMajorLayout && !this._invokingGallery && this.get("columnMajorLayout") 
                && !this.domNode.classList.contains("columnMajorLayout")) {
                this.updateColumnMajorLayout();
            }

            // Allow browser to render the nodes before adding scrollbars back
            setTimeout(function () {
                categoryContainer.style.overflowY = "auto";
            }.bind(this), 0);
        },

        _setDndEnabledAttr: function (value) {
            if (typeof value !== "boolean") {
                throw new Error("Expected input value to be of type 'boolean'.");
            }

            this._set("dndEnabled", value);

            if (value) {
                this._createDndSource();
            } else {
                this._destroyDndSource();
            }
        },

        _setListViewDisplayDensityAttr: function (value) {
            var isValid = false;
            Object.keys(ListViewDisplayDensityConstants).forEach(function (key) {
                this.domNode.classList.remove("mw" + capitalizeFirstLetter(key) + "ListViewDisplayDensity");

                if (value === ListViewDisplayDensityConstants[key]) {
                    isValid = true;
                }
            }.bind(this));

            if (isValid) {
                this._set("listViewDisplayDensity", value);
            }

            this.domNode.classList.add("mw" + capitalizeFirstLetter(this.get("listViewDisplayDensity")) + "ListViewDisplayDensity");
        },

        // Allow Categories to be dragged within the Gallery Popup
        _createDndSource: function () {
            if (this.dndEnabled) {
                // === Unregister category item container and category header as drop targets ===
                this._destroyDndSource();

                this._dragSorter = new DragSorter({
                    containerNode: this.containerNode,
                    dragOrientation: "vertical"
                });
                this._dragSorterListener = this._dragSorter.on("nodemoved", this._onContainerDropHandler.bind(this));
            }
        },

        _destroyDndSource: function () {
            if (this._dragSorter) {
                this._dragSorterListener.remove();
                delete this._dragSorterListener;
                this._dragSorter.destroy();
                delete this._dragSorter;
            }
        },

        /**
         * Callback attached to the 'onDrop' event of the Source. Updates the model according to the
         * new order of items in the view after the drag and drop has occurred.
         * @param data The dragSorter data.
         */
        _onContainerDropHandler: function (data) {
            var movedItemWidget = registry.getEnclosingWidget(data.node);
            movedItemWidget.categoryMoved({
                action: "Drop",
                itemId: movedItemWidget.id,
                newIndex: data.newIndex,
                originator: {source: "UI"}
            }, {
                widget: movedItemWidget,
                sourceData: this.containerNode,
                targetWidget: this
            });
        },

        _updateToggleButtons: function () {
            if (this.displayState === GalleryViewConstants.ICON) {
                if (this.iconViewButton && !this.iconViewButton.checked) {
                    this.iconViewButton.set("checked", true);
                }
            } else {
                if (this.listViewButton && !this.listViewButton.checked) {
                    this.listViewButton.set("checked", true);
                }
            }
        },

        _setFooterIdAttr: function (value) {
            // Cleanup the old footer
            if (this.footerId && (value !== this.footerId)) {
                if (this.ownsFooter) {
                    // Destroy the footer and footer children
                    var footerWidget = registry.byId(this.footerId);
                    if (footerWidget) {
                        footerWidget.destroyRecursive();
                    }
                } else {
                    // Orphan the footer to prevent Dojo from destroying it
                    while (this.footerContainer.firstChild) {
                        this.footerContainer.firstChild.remove();
                    }
                }
                // Temporarily clear the 'footerId' property so we don't end up in a bad state if error
                this._set("footerId", "");
            }

            // Set the new footer
            var footerWidget = registry.byId(value);
            if (footerWidget) {
                if (footerWidget.getChildren().length === 0) {
                    footerWidget.domNode.style.display = "none";
                }
                this.footerContainer.appendChild(footerWidget.domNode);
                //TODO: container
                footerWidget.startup();
                this.footerContainer.style.display = "block";

            } else {
                this.footerContainer.style.display = "none";
            }
            this._set("footerId", value);
        },

        _setFavCategoryIdAttr: function (value) {
            var favCategoryWidget = registry.byId(value);
            if (favCategoryWidget) {
                this._favCategoryWidget = favCategoryWidget;
                if (favCategoryWidget.getChildren().length === 0) {
                    favCategoryWidget.domNode.classList.add("hideFromView");
                }
                favCategoryWidget.set("isFavoriteCategory", true);
                favCategoryWidget.set("galleryPopupId", this.id);
                this.favCategoryContainer.appendChild(favCategoryWidget.domNode);
                this.own(favCategoryWidget);
                favCategoryWidget.set("displayState", this.displayState);
            }
            this._set("favCategoryId", value);
        },

        _setPlaceholderTextAttr: function(value){
            this.searchBox.set("placeholder", value);
        },

        _getPlaceholderTextAttr: function(){
            return this.searchBox.getAttribute("placeholder");
        },

        _setWidthAttr: function (value) {
            if (["number", "string"].indexOf(typeof value) < 0) {
                throw new Error("Expected a numeric or string value");
            } else if (typeof value === "number") {
                value = value <= 0 ? 0 : value + "px";
            } else { // "string"
                if (!((value === "auto") || (/[0-9]+.?([0-9]+)?(px|rem|em|%)$/.test(value)))) {
                    throw new Error("Expected a valid CSS descriptor value");
                }
            }

            this.domNode.style.width = value;
        },
        
        _getWidthAttr: function () {
            var value = this.domNode.style.width;
            
            return value ? "auto" : value;
        },

        _setHeightAttr: function (value) {
            if (["number", "string"].indexOf(typeof value) < 0) {
                throw new Error("Expected a numeric or string value");
            } else if (typeof value === "number") {
                value = value <= 0 ? 0 : value + "px";
            } else { // "string"
                if (!((value === "auto") || (/[0-9]+.?([0-9]+)?(px|rem|em|%)$/.test(value)))) {
                    throw new Error("Expected a valid CSS descriptor value");
                }
            }

            this.domNode.style.height = value;
        },

        _getHeightAttr: function () {
            var value = this.domNode.style.height;

            return value ? "auto" : value;
        },

        /**
         * A helper method which toggles the display of the passed in node.
         * @param node {domNode} The node whose display needs to be toggled.
         * @param value {boolean} The value can be true or false.
         */
        _toggleDisplay: function (node, value) {
            if (value) {
                node.style.display = "block";
            } else {
                node.style.display = "none";
            }
        },

        _handleSearchKeyDown: function (e) {
            if (e.keyCode === keys.ESCAPE && e.target.value !== "") {
                this._resetSearchBoxValue();
                this.searchBox.toggleSearchBoxIcon();
                e.stopPropagation();
            }
        },

        _handleSearchBoxTyping: function (e) {
            this._searchBoxValue = e.mwEventData.currentContent;
            this.enforceFilters();
        },

        _handleCompositionUpdate: function (e) {
            // There might be some inconsistencies between the actual value and inputNode.value as it takes some
            // time for the value to get updated in the inputNode when in IME mode. Use e.data as recommended in the
            // mdn web docs since it has the correct value.
            this._searchBoxValue = e.data;
            this.enforceFilters();
        },

        /**
         * Creates the supporting widgets: Search box, view toggle buttons and the close button.
         */
        _createSearchBoxAndButtons: function () {
            var that = this;

            this.searchBox.on("typing", this._handleSearchBoxTyping.bind(this));

            if (utils.isCJKLocale()) {
                // g1957116: Qt doesn't support sending key events during IME input. This results in GalleryItems
                // not getting filtered when a user types something. Even without this limitation, there would still
                // be another issue where user types something in IME mode and selects a text from the IME suggestions
                // popup using mouse. In this case, since it is not a key event the GalleryItems would still not be
                // filtered. Hence, we need to make use of "compositionupdate" event which gets fired when a new
                // character is received in the IME mode. This would ensure that both the use cases (typing and
                // selecting an item from IME suggestions popup) will filter the GalleryItems accordingly.
                this._listeners.push(utils.addEventListener(this.searchBox.inputNode, "compositionupdate",
                    this._handleCompositionUpdate.bind(this)));
            }

            this.searchBox.on("keydown", this._handleSearchKeyDown.bind(this));
            this.listViewButton = new ToggleButton({
                icon: 'icon_view_list_16',
                tag: 'listViewButton',
                buttonGroup: "galleryPopupView_" + this.id,
                description: toolstripL10n.listView
            });
            this.own(this.listViewButton);
            this.listViewButton.placeAt(this.listViewButtonContainerNode);
            this.listViewButton.on("click", function (e) {
                // Only give focus to the searchbox when the user "clicks" on the listViewButton. In case of click event
                // that is triggered due to "Enter" or "Space" key presses (e.clientX and e.clientY are "0" in this
                // case), do not change the focus.
                if (e.clientX > 0 && e.clientY > 0) {
                    this.focus();
                }
            }.bind(this));

            this.iconViewButton = new ToggleButton({
                icon: 'icon_view_icon_16',
                tag: 'iconViewButton',
                buttonGroup: "galleryPopupView_" + this.id,
                checked: true,
                description: toolstripL10n.iconView
            });
            this.own(this.iconViewButton);
            this.iconViewButton.placeAt(this.iconViewButtonContainerNode);
            this.iconViewButton.on("click", function (e) {
                // Only give focus to the searchbox when the user "clicks" on the iconViewButton. In case of click event
                // that is triggered due to "Enter" or "Space" key presses (e.clientX and e.clientY are "0" in this
                // case), do not change the focus.
                if (e.clientX > 0 && e.clientY > 0) {
                    this.focus();
                }
            }.bind(this));

            this.listViewButton.on("change", function (evt) {
                if (evt.mwEventData.propertyName === "checked" && evt.mwEventData.newValue === true) {
                    that.set("displayState", GalleryViewConstants.LIST);
                }
            });

            this.iconViewButton.on("change", function (evt) {
                if (evt.mwEventData.propertyName === "checked" && evt.mwEventData.newValue === true) {
                    that.set("displayState", GalleryViewConstants.ICON);
                }
            });

            this._updateToggleButtons();
        },

        _resetSearchBoxValue: function () {
            this.searchBox.set("value", "");
            this._searchBoxValue = "";
            this.enforceFilters();
        },

        focus: function () {
            if (this.searchBox && this.searchBox.focusNode && this.searchBox.focusNode.focus) {
                this.searchBox.focusNode.focus();
            }
        },

        _startupHeaderWidgets:function () {
            this.searchBox.startup();
            this.listViewButton.startup();
            this.iconViewButton.startup();
        },

        galleryStateChanged: function () {},

        _escapeRegExp: function(str){
            /*
             * Using the escape regular expression method from MDN
             * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#Using_special_characters
             * */
            return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        },

        destroy: function () {
            this._destroyDndSource();

            this._listeners.forEach(function (listener) {
                listener.remove();
            });

            this.set("footerId", "");

            this.inherited(arguments);
        },

        _qeTyping: function (value) {
            this.searchBox._qeTyping(value);
        }
    });
});